/**
 * Client-side noise filter for PostHog `$exception` capture.
 *
 * PostHog is the ONLY error tracker in this app. Its `capture_exceptions`
 * picks up every unhandled error, promise rejection, and `console.error`.
 * That firehose includes a large amount of noise we can neither fix nor
 * act on:
 *
 *   - Third-party marketing tags loaded via GTM (`gtm.js`): `jQuery is not
 *     defined`, `googletag is not defined`, `LiveChatWidget is not defined`,
 *     `MoEngage`, `WisePops`, `__tcfapi`, `webVitals.getCLS is not a function`.
 *   - A native mobile WebView bridge injected into the page
 *     (`window.webkit.messageHandlers`, `Java exception was raised ...`).
 *   - Browser-extension scripts (`chrome-extension://` / `moz-extension://`).
 *   - Benign browser chatter: cross-origin `Script error.` and
 *     `ResizeObserver loop ...`.
 *
 * Transport failures are handled separately and conditionally. Dropping every
 * `Failed to fetch` / `Load failed` by message alone also discarded our OWN
 * network failures: RELM-343 logged dozens of failed status polls that never
 * reached PostHog, so a 40-minute outage looked like silence. They are now
 * dropped only when nothing ties them to our code — see
 * `TRANSPORT_NOISE_PATTERNS` and `hasFirstPartyOrigin`.
 *
 * This is deliberately conservative: it matches only well-known third-party /
 * browser messages verified against real captured events. Application errors
 * (including handled ones surfaced via `console.error`) pass through untouched.
 *
 * @module lib/posthogErrorFilters
 */
import type { BeforeSendFn, CaptureResult } from 'posthog-js';
import { classifyServerActionError } from '@/lib/staleServerActionError';

/**
 * Exception messages / types that originate outside our code and are not
 * actionable. Matched (case-insensitive) against every `$exception` value and
 * type on the event. Keep entries specific — a broad pattern risks dropping
 * real application errors.
 */
const NOISE_PATTERNS: readonly RegExp[] = [
  // Cross-origin script errors with no useful detail (third-party CDNs).
  /^Script error\.?$/i,
  // Benign ResizeObserver loop notifications.
  /ResizeObserver loop/i,
  // Native mobile WebView bridge injected into the page — not our code.
  /window\.webkit\.messageHandlers/i,
  /Java exception was raised during method invocation/i,
  // Browser-extension injected scripts.
  /(?:chrome|moz|safari)-extension:\/\//i,
  // Third-party marketing / GTM tag globals (loaded via gtm.js, not our bundle).
  /\bgoogletag is not defined\b/i,
  /\bLiveChatWidget is not defined\b/i,
  /\bjQuery is not defined\b/i,
  /\bMoEngage\b/i,
  /\bWisePops\b/i,
  /\b__tcfapi\b/i,
  /webVitals\.getCLS is not a function/i,
];

/**
 * Browser implementation errors that are noise only without first-party
 * origin. Keep these fully anchored so a future app identifier stays visible.
 */
const CONDITIONAL_BROWSER_NOISE_PATTERNS: readonly RegExp[] = [
  // WebKit NullMedia bug observed on Mobile Safari with no app stack.
  /^\s*Can't find variable: EmptyRanges\.?\s*$/i,
];

/**
 * Transport failures, which are noise ONLY when nothing ties them to our code.
 *
 * A user going offline, cancelling a navigation, or an ad blocker killing a
 * vendor pixel are all unactionable. The same words from our own code are a
 * defect: RELM-343 left a report "analyzing" for 40+ minutes while its poll
 * failed repeatedly, and every one of those failures was dropped here before
 * PostHog ever saw it — the only thing that survived was a Next.js error whose
 * wording happened not to match this list.
 *
 * So the message alone cannot decide. `hasFirstPartyOrigin()` does.
 */
const TRANSPORT_NOISE_PATTERNS: readonly RegExp[] = [
  /Failed to fetch/i,
  /Load failed/i,
  /Network request failed/i,
  /NetworkError when attempting to fetch/i,
  /The operation was aborted/i,
  /\bAbortError\b/i,
];

/**
 * Log prefix used by our own `console.error` calls — 224 call sites follow
 * `[module] message`, which makes it a reliable origin marker for the
 * console-derived exceptions that arrive with no usable stack.
 */
const FIRST_PARTY_LOG_PREFIX = /^\s*\[[A-Za-z][\w-]*\]/;

/** App-owned paths that can appear in browser frames or source maps. */
const FIRST_PARTY_URL_PATH =
  /^\/(?:_next|app|components|contexts|hooks|lib|services)(?:\/|$)/i;
const FIRST_PARTY_SOURCE_ROOT =
  /^(?:app|components|contexts|hooks|lib|services)(?:\/|$)/i;
const FIRST_PARTY_REPOSITORY_PATH =
  /(?:^|\/)(?:apps\/pro|packages\/ui)(?:\/|$)/i;
const SOURCE_MAP_PATH_PREFIX =
  /^(?:(?:webpack(?:-internal)?|turbopack):\/{2,3}(?:_N_E\/)?(?:\[project\]\/)?|file:\/\/)/i;
/** Next 16 `WEBPACK_LAYERS_NAMES`, which may wrap a source-map path. */
const NEXT_SOURCE_LAYER_PREFIX =
  /^\((?:shared|rsc|ssr|action-browser|api-node|api-edge|middleware|instrument|edge-asset|app-pages-browser|pages-dir-browser|pages-dir-edge|pages-dir-node)\)\//;
const HTTP_FRAME_URL = /^(?:https?:)?\/\//i;

type NormalizationState = {
  isMalformed: boolean;
};

type NormalizedExceptionReason = {
  __NEXT_ERROR_CODE?: string;
  hasFirstPartyOrigin: boolean;
  message?: string;
  name?: string;
  stack?: string;
};

type NormalizedExceptionSource = {
  isMalformed: boolean;
  reasons: NormalizedExceptionReason[];
};

type OwnPropertyRead = {
  isPresent: boolean;
  value?: unknown;
};

function readProperty(
  source: object,
  property: PropertyKey,
  state: NormalizationState,
): unknown {
  try {
    return Reflect.get(source, property);
  } catch {
    state.isMalformed = true;
    return undefined;
  }
}

function readOwnProperty(
  source: object,
  property: PropertyKey,
  state: NormalizationState,
): OwnPropertyRead {
  try {
    if (!Object.prototype.hasOwnProperty.call(source, property)) {
      return { isPresent: false };
    }
  } catch {
    state.isMalformed = true;
    return { isPresent: true };
  }

  return {
    isPresent: true,
    value: readProperty(source, property, state),
  };
}

function readOptionalString(
  source: object,
  property: PropertyKey,
  state: NormalizationState,
): string | undefined {
  const value = readProperty(source, property, state);
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;

  state.isMalformed = true;
  return undefined;
}

function isRecognizedFirstPartySourcePath(location: string): boolean {
  if (HTTP_FRAME_URL.test(location)) return false;

  let normalizedPath = location.replace(/[?#].*$/, '').replaceAll('\\', '/');
  const hasSourceMapPrefix = SOURCE_MAP_PATH_PREFIX.test(normalizedPath);
  normalizedPath = normalizedPath.replace(SOURCE_MAP_PATH_PREFIX, '');
  if (hasSourceMapPrefix) {
    normalizedPath = normalizedPath.replace(NEXT_SOURCE_LAYER_PREFIX, '');
  }
  normalizedPath = normalizedPath.replace(/^\.?\//, '');

  if (/(?:^|\/)node_modules\//i.test(normalizedPath)) return false;

  return (
    FIRST_PARTY_SOURCE_ROOT.test(normalizedPath) ||
    FIRST_PARTY_REPOSITORY_PATH.test(normalizedPath)
  );
}

function parseHttpUrl(value: string, base?: URL): URL | undefined {
  try {
    const parsedUrl = base ? new URL(value, base) : new URL(value);
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return undefined;
    }
    return parsedUrl;
  } catch {
    return undefined;
  }
}

/**
 * Prove a frame belongs to this app from its URL or source-map path.
 *
 * posthog-js marks every parsed browser frame `in_app: true`, including vendor
 * and runtime frames, so that field is deliberately not a provenance signal.
 */
function isFirstPartyFrameLocation(
  location: string,
  currentUrl: string | undefined,
): boolean {
  if (isRecognizedFirstPartySourcePath(location)) return true;

  const pageUrl = currentUrl ? parseHttpUrl(currentUrl) : undefined;
  if (!pageUrl) return false;

  const frameUrl = parseHttpUrl(location, pageUrl);
  return (
    frameUrl !== undefined &&
    frameUrl.origin === pageUrl.origin &&
    FIRST_PARTY_URL_PATH.test(frameUrl.pathname)
  );
}

function normalizeStacktrace(
  item: object,
  state: NormalizationState,
  currentUrl: string | undefined,
): Pick<NormalizedExceptionReason, 'hasFirstPartyOrigin' | 'stack'> {
  const stacktrace = readProperty(item, 'stacktrace', state);
  if (stacktrace === undefined || stacktrace === null) {
    return { hasFirstPartyOrigin: false };
  }
  if (typeof stacktrace !== 'object') {
    state.isMalformed = true;
    return { hasFirstPartyOrigin: false };
  }

  const frames = readProperty(stacktrace, 'frames', state);
  if (frames === undefined || frames === null) {
    return { hasFirstPartyOrigin: false };
  }
  if (!Array.isArray(frames)) {
    state.isMalformed = true;
    return { hasFirstPartyOrigin: false };
  }

  let hasFirstPartyOrigin = false;
  const stackLines: string[] = [];
  try {
    for (const frame of frames) {
      if (frame === null || typeof frame !== 'object') {
        state.isMalformed = true;
        continue;
      }

      const filename = readOptionalString(frame, 'filename', state);
      const absolutePath = readOptionalString(frame, 'abs_path', state);
      const functionName = readOptionalString(frame, 'function', state);
      const locations = new Set<string>();
      if (filename !== undefined) locations.add(filename);
      if (absolutePath !== undefined) locations.add(absolutePath);

      if (
        [...locations].some((location) =>
          isFirstPartyFrameLocation(location, currentUrl),
        )
      ) {
        hasFirstPartyOrigin = true;
      }

      for (const location of locations) {
        stackLines.push(`    at ${functionName || '?'} (${location})`);
      }
    }
  } catch {
    state.isMalformed = true;
  }

  return {
    hasFirstPartyOrigin,
    stack: stackLines.length > 0 ? stackLines.join('\n') : undefined,
  };
}

function normalizeListItem(
  item: unknown,
  state: NormalizationState,
  currentUrl: string | undefined,
): NormalizedExceptionReason | null {
  if (item === null || typeof item !== 'object') {
    state.isMalformed = true;
    return null;
  }

  const type = readOptionalString(item, 'type', state);
  const name = readOptionalString(item, 'name', state);
  const value = readOptionalString(item, 'value', state);
  const message = readOptionalString(item, 'message', state);
  const code = readOptionalString(item, '__NEXT_ERROR_CODE', state);
  const rawStack = readOptionalString(item, 'stack', state);
  const parsedStack = normalizeStacktrace(item, state, currentUrl);
  const stack = [rawStack, parsedStack.stack].filter(Boolean).join('\n');

  return {
    __NEXT_ERROR_CODE: code,
    hasFirstPartyOrigin: parsedStack.hasFirstPartyOrigin,
    message: value ?? message,
    name: type ?? name,
    stack: stack || undefined,
  };
}

function normalizeList(
  list: unknown[],
  state: NormalizationState,
  currentUrl: string | undefined,
): NormalizedExceptionReason[] {
  const reasons: NormalizedExceptionReason[] = [];
  try {
    for (const item of list) {
      const reason = normalizeListItem(item, state, currentUrl);
      if (reason !== null) reasons.push(reason);
    }
  } catch {
    state.isMalformed = true;
  }
  return reasons;
}

function readFallbackArray(
  properties: object,
  property: '$exception_types' | '$exception_values',
  state: NormalizationState,
): unknown[] {
  const result = readOwnProperty(properties, property, state);
  if (!result.isPresent) return [];
  if (Array.isArray(result.value)) return result.value;

  state.isMalformed = true;
  return [];
}

function normalizeFallbackArrays(
  properties: object,
  state: NormalizationState,
): NormalizedExceptionReason[] {
  const types = readFallbackArray(properties, '$exception_types', state);
  const values = readFallbackArray(properties, '$exception_values', state);
  let pairCount = 0;
  try {
    pairCount = Math.max(types.length, values.length);
  } catch {
    state.isMalformed = true;
  }

  const reasons: NormalizedExceptionReason[] = [];
  for (let index = 0; index < pairCount; index += 1) {
    reasons.push({
      hasFirstPartyOrigin: false,
      message: readOptionalString(values, index, state),
      name: readOptionalString(types, index, state),
    });
  }
  return reasons;
}

/**
 * Select and normalize one authoritative PostHog exception source.
 *
 * A present `$exception_list` wins even when malformed. Falling through to the
 * parallel arrays in that case could suppress an event using contradictory
 * stale-looking fallback data.
 */
function normalizeExceptionSource(
  event: CaptureResult,
): NormalizedExceptionSource {
  const state: NormalizationState = { isMalformed: false };
  const properties = readProperty(event, 'properties', state);
  if (properties === undefined || properties === null) {
    return { isMalformed: state.isMalformed, reasons: [] };
  }
  if (typeof properties !== 'object') {
    return { isMalformed: true, reasons: [] };
  }

  const currentUrl = readOptionalString(properties, '$current_url', state);
  const list = readOwnProperty(properties, '$exception_list', state);
  if (list.isPresent) {
    if (!Array.isArray(list.value)) {
      return { isMalformed: true, reasons: [] };
    }
    const reasons = normalizeList(list.value, state, currentUrl);
    return { isMalformed: state.isMalformed, reasons };
  }

  const reasons = normalizeFallbackArrays(properties, state);
  return { isMalformed: state.isMalformed, reasons };
}

/** Collect every human-readable exception message/type string on a source. */
function collectExceptionMessages(source: NormalizedExceptionSource): string[] {
  const messages: string[] = [];
  for (const reason of source.reasons) {
    if (reason.message !== undefined) messages.push(reason.message);
    if (reason.name !== undefined) messages.push(reason.name);
  }
  return messages;
}

/**
 * Whether anything on the event ties the error to code we wrote.
 *
 * Frame provenance comes from same-origin app URLs or recognized source paths.
 * A console-derived exception frequently arrives with no stack at all, so the
 * app's log prefix is also checked. This runs in `before_send`, so the RAW
 * frames are still present; the processed `$exception_sources` seen in the
 * warehouse is usually empty by comparison.
 */
function hasFirstPartyOrigin(source: NormalizedExceptionSource): boolean {
  if (source.reasons.some((reason) => reason.hasFirstPartyOrigin)) return true;

  return collectExceptionMessages(source).some((message) =>
    FIRST_PARTY_LOG_PREFIX.test(message),
  );
}

/**
 * True when the event is a `$exception` we can neither fix nor act on.
 *
 * Stale Server Actions and third-party patterns drop unconditionally.
 * Conditional browser and transport failures drop only when nothing marks
 * them as ours. Non-exception events are never noisy.
 */
export function isNoisyException(event: CaptureResult): boolean {
  if (event.event !== '$exception') return false;

  const source = normalizeExceptionSource(event);
  // If the payload cannot be inspected safely, keep it rather than guessing.
  if (source.isMalformed) return false;

  // PostHog emits chained causes as separate list entries. Any actionable
  // signal keeps the whole event, regardless of where a stale cause appears.
  let hasStaleServerAction = false;
  for (const reason of source.reasons) {
    const classification = classifyServerActionError(reason);
    if (classification === 'retained') return false;
    if (classification === 'stale') hasStaleServerAction = true;
  }
  if (hasStaleServerAction) return true;

  const messages = collectExceptionMessages(source);
  if (messages.length === 0) return false;

  // Check unconditional patterns first: a first-party EmptyRanges cause must
  // not mask separate extension/vendor noise in the same chained event.
  const thirdParty = messages.some((message) =>
    NOISE_PATTERNS.some((pattern) => pattern.test(message)),
  );
  if (thirdParty) return true;

  const hasFirstParty = hasFirstPartyOrigin(source);
  const conditionalBrowser = messages.some((message) =>
    CONDITIONAL_BROWSER_NOISE_PATTERNS.some((pattern) => pattern.test(message)),
  );
  if (conditionalBrowser && !hasFirstParty) return true;

  const transport = messages.some((message) =>
    TRANSPORT_NOISE_PATTERNS.some((pattern) => pattern.test(message)),
  );
  if (!transport) return false;

  // Our own transport failure is a defect, not noise. Keep it.
  return !hasFirstParty;
}

/**
 * PostHog `before_send` hook: drop known third-party / browser-noise
 * exceptions, pass everything else through unchanged. Returning `null` drops
 * the event.
 */
export const dropNoisyExceptions: BeforeSendFn = (event) => {
  if (event && isNoisyException(event)) return null;
  return event;
};
